import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { DACH_TYPEN, GENEIGTE_DACHFORMEN } from "./enums";
import { ILLU } from "./illustrationTokens";

const useStyles = makeStyles({
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "12px",
    "@media (min-width: 720px)": {
      gridTemplateColumns: "repeat(4, 1fr)",
    },
  },
  tile: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    paddingTop: "16px",
    paddingBottom: "12px",
    paddingLeft: "12px",
    paddingRight: "12px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    rowGap: "10px",
    minHeight: "150px",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    ...shorthands.border("2px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    transitionProperty: "border-color, background-color, transform",
    transitionDuration: "150ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      backgroundColor: "#f5eef6",
      transform: "translateY(-2px)",
    },
  },
  tileActive: {
    ...shorthands.borderColor(BRAND.purple),
    backgroundColor: "#f5eef6",
    boxShadow: `0 0 0 3px ${BRAND.purple}22`,
  },
  illust: {
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  label: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    fontWeight: 600,
  },
  detail: {
    marginTop: "20px",
    paddingTop: "16px",
    paddingLeft: "20px",
    paddingRight: "20px",
    paddingBottom: "16px",
    backgroundColor: "#FFF8E8",
    ...shorthands.borderLeft("3px", "solid", BRAND.gold),
    ...shorthands.borderRadius("4px"),
  },
  detailLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: BRAND.muted,
    marginBottom: "8px",
  },
  radioRow: {
    display: "flex",
    columnGap: "20px",
  },
  radioOption: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
  },
  radioInput: {
    accentColor: BRAND.purple,
    cursor: "pointer",
  },
});

// Farben zentral, damit Dach- und Bauartklassen-Schritt eine Bildsprache sind.
const ROOF_FILL = ILLU.roofFill;
const ROOF_STROKE = ILLU.roofStroke;

const Satteldach = () => (
  <svg width="100" height="80" viewBox="0 0 100 80" fill="none">
    <line x1="6" y1="74" x2="94" y2="74" stroke={ROOF_STROKE} strokeWidth="1.5" />
    <path d="M16 74V36L50 12l34 24v38z" fill={ROOF_FILL} stroke={ROOF_STROKE} strokeWidth="1.5" strokeLinejoin="round" />
    <line x1="16" y1="36" x2="84" y2="36" stroke={ROOF_STROKE} strokeWidth="1" strokeDasharray="3 2" />
    <text x="6" y="32" fontSize="9" fill={BRAND.muted} fontFamily={BRAND.fontPrimary}>TH</text>
  </svg>
);

const Pultdach = () => (
  <svg width="100" height="80" viewBox="0 0 100 80" fill="none">
    <line x1="6" y1="74" x2="94" y2="74" stroke={ROOF_STROKE} strokeWidth="1.5" />
    <path d="M14 74V40L82 22v52z" fill={ROOF_FILL} stroke={ROOF_STROKE} strokeWidth="1.5" strokeLinejoin="round" />
    <text x="86" y="20" fontSize="9" fill={BRAND.muted} fontFamily={BRAND.fontPrimary}>+1m</text>
  </svg>
);

const Flachdach = () => (
  <svg width="100" height="80" viewBox="0 0 100 80" fill="none">
    <line x1="6" y1="74" x2="94" y2="74" stroke={ROOF_STROKE} strokeWidth="1.5" />
    <path d="M14 74V36h12v6H82v-6h12v38z" fill={ROOF_FILL} stroke={ROOF_STROKE} strokeWidth="1.5" strokeLinejoin="round" />
    <text x="30" y="34" fontSize="9" fill={BRAND.muted} fontFamily={BRAND.fontPrimary}>Attika</text>
  </svg>
);

const Staffelgeschoss = () => (
  <svg width="100" height="80" viewBox="0 0 100 80" fill="none">
    <line x1="6" y1="74" x2="94" y2="74" stroke={ROOF_STROKE} strokeWidth="1.5" />
    <rect x="14" y="46" width="72" height="28" fill={ROOF_FILL} stroke={ROOF_STROKE} strokeWidth="1.5" />
    <rect x="34" y="22" width="40" height="24" fill={ROOF_FILL} stroke={ROOF_STROKE} strokeWidth="1.5" />
    <text x="78" y="20" fontSize="9" fill={BRAND.muted} fontFamily={BRAND.fontPrimary}>OK</text>
  </svg>
);

const ICONS: Record<string, () => JSX.Element> = {
  Satteldach,
  Pultdach,
  Flachdach,
  Staffelgeschoss,
};

type Props = {
  value: string;
  ausgebaut: string;
  onChange: (next: string) => void;
  onAusgebautChange: (next: string) => void;
};

const DachPicker = ({ value, ausgebaut, onChange, onAusgebautChange }: Props) => {
  const styles = useStyles();
  return (
    <div>
      <div className={styles.grid}>
        {DACH_TYPEN.map((t) => {
          const Icon = ICONS[t];
          const active = value === t;
          return (
            <button
              type="button"
              key={t}
              className={mergeClasses(styles.tile, active && styles.tileActive)}
              onClick={() => onChange(t)}
            >
              <div className={styles.illust}>
                <Icon />
              </div>
              <div className={styles.label}>{t}</div>
            </button>
          );
        })}
      </div>
      {GENEIGTE_DACHFORMEN.includes(value) && (
        <div className={styles.detail}>
          <div className={styles.detailLabel}>Dachgeschoss</div>
          <div className={styles.radioRow}>
            {[
              { v: "ausgebaut", label: "ausgebaut" },
              { v: "nicht_ausgebaut", label: "nicht ausgebaut" },
              { v: "teilweise", label: "teilweise" },
            ].map((opt) => (
              <label key={opt.v} className={styles.radioOption}>
                <input
                  type="radio"
                  className={styles.radioInput}
                  checked={ausgebaut === opt.v}
                  onChange={() => onAusgebautChange(opt.v)}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DachPicker;
